/**
* @file Table of Content
* @author Ice(chenzhouji@baidu.com)
*/
import _ from 'lodash';
import {navigate} from 'gatsby';
import React, {FunctionComponent, MouseEventHandler, useState, useEffect, useRef, useCallback} from 'react';
import useEventListener from '@use-it/event-listener';
import classnames from 'classnames';
import {LinkItem} from './types';
import {headerHeightMap} from '@/constants';

const SITE_HEADER_HEIGHT = headerHeightMap["acg-portal-en"];

type LinkItemProps = {
    item: LinkItem
    onClick: MouseEventHandler
    isActive: boolean
};

const Link: FunctionComponent<LinkItemProps> = ({
    item: {path, name, depth},
    onClick,
    isActive
}) => (
    <li
        className={classnames({
            'toc-menu__item': true,
            [`toc-menu__item--level-${depth}`]: true,
            'toc-menu__item--active': isActive,
        })}
        key={JSON.stringify(name)}
    >
        <a
            className="link"
            href={path}
            onClick={onClick}
            style={{paddingLeft: 10 * (depth - 1) + 5}}
        >{name}</a>
    </li>
);

type TOCProps = {
    items: LinkItem[]
    defaultHash: string,
    template?: string
};

const getAnchorElm = (encodedHash: string): (HTMLAnchorElement|null) => {
    const selector = `a.anchor[href="${encodedHash}"]`;
    return document.querySelector(selector);
};

const getOffsetRight = () => {
    let right = 0;

    try {
        const sideEl: any = window.document.querySelector('.sider');
        right = sideEl && sideEl.offsetLeft || 0;
    }
    catch (error) {
        right  = 0;
    }

    return right;
};

const TOC: FunctionComponent<TOCProps> = ({items, defaultHash, template = 'default'}) => {
    if (!items.length) {
        return null;
    }

    const toc:any = useRef(null);

    let isMount = useRef(false);

    // click会触发 onScroll -> 上一个useEffect的return -> 执行本次useEffect。
    // 当n段section同时出现这viewport中，根据onScroll的高亮逻辑，最后一个section的标题高亮。（scroll高亮逻辑）
    // 而通过点击anchor滚动后，标题是viewport最上面的第一个，即我们的期望高亮标题（click高亮逻辑）
    // 所以在onScroll执行的时候，我们的期望高亮标题会被覆盖掉。
    // **因此，如果是click触发的scroll，则不执行 _scroll高亮逻辑_ **
    // !-----emmmmm....交互改成标题到顶部才高亮，上面的注释没用了 -----!
    let byClick = useRef(false);

    const initialItem = decodeURI(defaultHash) || items[0].path;
    const [activeItem, setActiveItem] = useState(initialItem);

    // 为什么初始值设为 0 而不是 用getOffsetRight()计算出？
    // ssr的缘故server端值为0，当client端初始值设为getOffsetRight计算出的X时，
    // 等useEffect执行完setOffsetRight并不会等触发re-render，因为值仍为X->X没有变化。
    // 只有当 0->X 的变化才能触发re-render。
    const [offsetRight, setOffsetRight] = useState(0);
    const [height, setHeight] = useState('auto');

    const postOffsetTop = SITE_HEADER_HEIGHT + 84;

    const initPosition = () => {
        if (isMount.current === false) {
            const elm = getAnchorElm(window.location.hash);
            window.scrollTo(0, elm ? elm.offsetTop - SITE_HEADER_HEIGHT: 0);
        }
    };

    const handleLinkClick: MouseEventHandler = (e: any) => {
        setActiveItem(`${decodeURI(e.target.hash)}`);
    };

    // --- resize effect ---
    const updateOffsetRight = useCallback(() => {
        const offsetRight = getOffsetRight();
        setOffsetRight(offsetRight);
    }, [setOffsetRight]);

    const updateHeight = useCallback(() => {
        const height = (window.innerHeight - postOffsetTop) / 2;
        setHeight(`${height}px`);
    }, [setHeight]);

    const onResize = () => {
        updateOffsetRight();
        updateHeight();
    };

    useEventListener('resize', onResize);

    // --- scroll effect ---
    const onScroll = useCallback(() => {
        if (byClick.current) {
            byClick.current = false;
            return;
        }

        const scrollTop = document.body.getBoundingClientRect().top;

        if (scrollTop < -(SITE_HEADER_HEIGHT + 20)) {
            toc.current.className = "toc fixed"
        }
        else {
            toc.current.className = "toc"
        }

        _.forEach(items, (item, index) => {
            const anchorElmCurrent = getAnchorElm(encodeURI(item.path));
            const anchorElmNext = index !== items.length - 1
                ? getAnchorElm(encodeURI(items[index + 1].path))
                : null;

            const offset = anchorElmCurrent ? anchorElmCurrent.offsetTop : 0;
            const height = anchorElmCurrent && anchorElmNext
                ? anchorElmNext.offsetTop - anchorElmCurrent.offsetTop
                : (anchorElmCurrent
                    ? anchorElmCurrent.offsetHeight
                    : 0
                );

            // 段落标题到达顶部 -> 整个段落过顶
            if (offset <= -scrollTop && height + offset > -scrollTop) {
                setActiveItem(item.path);
                // FIXME: 用createRef不生效，暂时这么办吧
                const tocElm = document.querySelector('.toc');
                const linkElm: (HTMLAnchorElement|null) = document.querySelector(`a.link[href="${item.path}"]`);
                const linkElmTop = linkElm ? linkElm.offsetTop : 0;
                const tocElmHeight = tocElm ? tocElm.clientHeight : 0;

                // toc里居中展示link
                const top = linkElmTop - (tocElmHeight / 2);
                tocElm && tocElm.scrollTo(0, top);
            }
        });
    }, [items]);

    useEventListener('scroll', onScroll);

    useEffect(() => {
        updateHeight();
        updateOffsetRight();
        initPosition();
    }, [updateHeight, updateOffsetRight]);

    // 当offset没计算出前先隐藏，能获得更好的视觉效果
    // const opacity = offsetRight ? 1 : 0;
    const opacity = 1;
    const navStyle = {right: offsetRight, height, top: postOffsetTop, opacity};

    return (
        <nav className="toc" ref={toc} style={navStyle}>
            <div className="title">Catalogue</div>
            <ul className="toc-menu">
                {items.map(item => item.depth > 1
                    ? (
                        <Link
                            key={item.path}
                            item={item}
                            isActive={item.path.toLowerCase() === activeItem.toLowerCase()}
                            onClick={handleLinkClick}
                        />
                    )
                    : null
                )}
            </ul>
        </nav>
    );
};

export default TOC;
